/**
 * Optimized Create Label Component
 * Features comprehensive error handling, real-time validation, loading states, and UX improvements
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import Skeleton, { FormSkeleton, PackageFormSkeleton, AddressValidationSkeleton } from '../components/ui/Skeleton';
import ServiceStatusMonitor from '../components/ServiceStatusMonitor';
import { optimizedApiService } from '../lib/apiOptimized';
import { authAPI } from '../lib/api';
import { errorHandler, withErrorHandling } from '../lib/errorHandler';
import { useLoadingStates } from '../hooks/useLoadingStates';
import { useAuth } from '../contexts/AuthContext';
import { 
  LabelData,
  addressValidationUtils, 
  packageValidationUtils,
  useRealTimeValidation,
  validateUSPostalCode,
  validateUSState
} from '../lib/formValidation';
import { 
  Package, MapPin, Truck, Plus, Trash2, 
  CheckCircle, AlertCircle, Loader2, Download, Printer,
  Info, Clock, Shield, Zap, DollarSign
} from 'lucide-react';
import toast from 'react-hot-toast';
import { UserProfile } from '../types';

interface PackageItem {
  id: string;
  weight: number;
  weight_unit: 'lb' | 'kg' | 'oz' | 'g';
  length: number;
  width: number;
  height: number;
  dimension_unit: 'in' | 'cm';
  description: string;
  quantity: number;
}

interface CreateLabelForm {
  // To Address
  to_name: string;
  to_company: string;
  to_address1: string;
  to_address2?: string;
  to_city: string;
  to_state: string;
  to_postal: string;
  to_country: string;
  to_phone: string;
  to_email: string;
  to_residential: boolean;

  // Packages
  packages: PackageItem[];

  // Service
  service_type: string;
  reference_number?: string;
  reference_number_2?: string;
  description?: string;
  declared_value?: number;
}

interface AddressValidationResult {
  valid: boolean;
  suggestions: any[];
  fallback_used?: boolean;
  message?: string;
}

const CreateLabelOptimized: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Loading states management
  const { 
    states: loadingStates, 
    setLoading, 
    setSuccess, 
    setError, 
    execute: executeWithLoading 
  } = useLoadingStates();

  // Fetch user profile to check rate display settings
  const { data: userProfile } = useQuery<UserProfile>({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const response = await authAPI.getUserInfo();
      return response.data as UserProfile;
    },
    staleTime: 60000, // Cache for 1 minute
  });

  // Form management
  const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm<CreateLabelForm>({
    defaultValues: {
      to_country: 'US',
      to_residential: false,
      service_type: '03', // Ground service
      packages: [{
        id: '1',
        weight: 1,
        weight_unit: 'lb',
        length: 12,
        width: 10,
        height: 8,
        dimension_unit: 'in',
        description: '',
        quantity: 1
      }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'packages'
  });

  // Validation rules
  const validationRules = {
    to_name: { required: true, minLength: 1, maxLength: 100 },
    to_address1: { required: true, minLength: 1, maxLength: 100 },
    to_city: { required: true, minLength: 1, maxLength: 50 },
    to_state: { required: true, state: true },
    to_postal: { required: true, postalCode: true },
    service_type: { required: true }
  };

  // Package validation rules
  const packageValidationRules = {
    weight: { required: true, min: 0.1, max: 150 },
    quantity: { required: true, min: 1, max: 99 },
    length: { required: true, min: 0.1, max: 108 },
    width: { required: true, min: 0.1, max: 108 },
    height: { required: true, min: 0.1, max: 108 }
  };

  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [addressValidation, setAddressValidation] = useState<AddressValidationResult | null>(null);
  const [serviceOptions, setServiceOptions] = useState<any[]>([]);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [shippingRates, setShippingRates] = useState<any>({});
  const [isCalculatingRates, setIsCalculatingRates] = useState(false);

  // Watch form values for real-time validation
  const watchedValues = watch();

  // Custom validation for packages
  const validatePackages = useCallback(() => {
    if (!fields || fields.length === 0) {
      return false;
    }

    return fields.every((pkg: any, index: number) => {
      const weight = watchedValues.packages?.[index]?.weight;
      const length = watchedValues.packages?.[index]?.length;
      const width = watchedValues.packages?.[index]?.width;
      const height = watchedValues.packages?.[index]?.height;
      const quantity = watchedValues.packages?.[index]?.quantity;

      return (
        weight && weight >= 0.1 && weight <= 150 &&
        length && length >= 0.1 && length <= 108 &&
        width && width >= 0.1 && width <= 108 &&
        height && height >= 0.1 && height <= 108 &&
        quantity && quantity >= 1 && quantity <= 99
      );
    });
  }, [fields, watchedValues.packages]);

  // Use form's built-in validation instead of custom real-time validation
  const isAddressValid = !Object.keys(errors).length && watchedValues.to_name && watchedValues.to_address1 && watchedValues.to_city && watchedValues.to_state && watchedValues.to_postal && watchedValues.to_phone;
  const isServiceValid = watchedValues.service_type;
  const isFormValid = isAddressValid && validatePackages() && isServiceValid;

  // Combined form validation (kept for backward compatibility)
  const isFormValidWithPackages = isFormValid;

  // Load service options with rates (if enabled for user)
  useEffect(() => {
    const loadServiceOptions = async () => {
      try {
        setLoading('load_services', true);
        // Basic service options
        const services = [
          { code: '01', name: 'Next Day Air', description: 'Next business day delivery' },
          { code: '02', name: '2nd Day Air', description: 'Second business day delivery' },
          { code: '03', name: 'Ground', description: 'Ground delivery (1-5 business days)' },
          { code: '12', name: '3 Day Select', description: 'Third business day delivery' },
          { code: '13', name: 'Next Day Air Saver', description: 'Next business day delivery (end of day)' },
          { code: '14', name: 'Next Day Air Early A.M.', description: 'Next business day delivery (early morning)' },
          { code: '59', name: '2nd Day Air A.M.', description: 'Second business day delivery (morning)' },
          { code: '65', name: 'Saver', description: 'Economy ground service' },
        ];
        setServiceOptions(services);
        setSuccess('load_services');
      } catch (error) {
        setError('load_services', 'Failed to load service options');
      }
    };

    loadServiceOptions();
  }, [setLoading, setSuccess, setError]);

  // Address validation with debouncing
  const validateAddress = useCallback(async () => {
    if (!watchedValues.to_address1 || !watchedValues.to_city || !watchedValues.to_state || !watchedValues.to_postal) {
      return;
    }

    try {
      setIsValidatingAddress(true);
      const apiResponse: any = await optimizedApiService.addresses.validateAddress({
        name: watchedValues.to_name,
        address_line_1: watchedValues.to_address1,
        address_line_2: watchedValues.to_address2 || '',
        city: watchedValues.to_city,
        state_province: watchedValues.to_state,
        postal_code: watchedValues.to_postal,
        country_code: watchedValues.to_country,
        phone: watchedValues.to_phone || '',
        email: watchedValues.to_email || '',
      });

      // The API returns is_valid, not valid
      const rawResponse: any = apiResponse.data || apiResponse;
      
      // Map the API response to our expected format
      const response: AddressValidationResult = {
        valid: rawResponse.is_valid || rawResponse.valid || false,
        suggestions: rawResponse.suggestions || [],
        fallback_used: rawResponse.fallback_used || false,
        message: rawResponse.message || ''
      };

      setAddressValidation(response);

      if (response.valid === true) {
          toast.success('Address validated successfully');
      } else if (response.fallback_used === true) {
        toast('Address validation service unavailable, proceeding with entered address', { icon: 'ℹ️' });
        } else {
        toast('Address may need correction', { icon: '⚠️' });
        }
      } catch (error) {
      console.error('Address validation error:', error);
      toast.error('Address validation failed');
      } finally {
        setIsValidatingAddress(false);
      }
  }, [watchedValues.to_name, watchedValues.to_address1, watchedValues.to_address2, watchedValues.to_city, watchedValues.to_state, watchedValues.to_postal, watchedValues.to_country, watchedValues.to_phone, watchedValues.to_email]);

  // Auto-validate address when all required fields are filled
  useEffect(() => {
    // Debounce the validation - only validate after user stops typing for 1 second
    const timer = setTimeout(() => {
      if (watchedValues.to_name && watchedValues.to_address1 && watchedValues.to_city && watchedValues.to_state && watchedValues.to_postal) {
        validateAddress();
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(timer);
  }, [watchedValues.to_name, watchedValues.to_address1, watchedValues.to_city, watchedValues.to_state, watchedValues.to_postal, validateAddress]);

  // Create label mutation
  const createLabelMutation = useMutation({
    mutationFn: (data: any) => optimizedApiService.shipments.createShipment(data),
    onSuccess: () => {
      toast.success('Label created successfully!');
      queryClient.invalidateQueries({ queryKey: ['labels'] });
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      navigate('/labels');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create label';
      toast.error(errorMessage);
      errorHandler.handleApiError(error, 'Create Label');
    }
  });

  // Calculate rates when moving to Step 3
  const calculateRates = useCallback(async () => {
    // Always fetch rates to determine available services, but control display
    setIsCalculatingRates(true);
    try {
      const rateData = {
        to_name: watchedValues.to_name,
        to_company: watchedValues.to_company || '',
        to_address1: watchedValues.to_address1,
        to_address2: watchedValues.to_address2 || '',
        to_city: watchedValues.to_city,
        to_state: watchedValues.to_state,
        to_postal: watchedValues.to_postal,
        to_country: watchedValues.to_country,
        to_phone: watchedValues.to_phone,
        to_residential: watchedValues.to_residential || false,
        packages: watchedValues.packages.map(pkg => ({
          weight: parseFloat(pkg.weight.toString()),
          weight_unit: pkg.weight_unit,
          length: parseFloat(pkg.length.toString()),
          width: parseFloat(pkg.width.toString()),
          height: parseFloat(pkg.height.toString()),
          dimension_unit: pkg.dimension_unit,
          description: pkg.description || '',
          quantity: parseInt(pkg.quantity.toString())
        }))
      };

      const response: any = await optimizedApiService.shipments.calculateRates(rateData);
      
      if (response.success && response.rates) {
        // Convert array to object keyed by service_code for easy lookup
        const ratesMap: any = {};
        response.rates.forEach((rate: any) => {
          ratesMap[rate.service_code] = rate;
        });
        setShippingRates(ratesMap);
        
        // Update serviceOptions to ONLY show services that have rates available
        // This ensures users only see services that are actually available for their shipment
        const availableServices = response.rates.map((rate: any) => ({
          code: rate.service_code,
          name: rate.service_name,
          description: rate.service_name
        }));
        setServiceOptions(availableServices);
        
        // Show success message based on rate display permission
        if (response.show_rates_to_user) {
          toast.success('Shipping rates calculated successfully');
        } else {
          toast.success('Available shipping methods loaded');
        }
      } else {
        toast.error(response.error || 'Failed to calculate rates');
      }
    } catch (error: any) {
      toast.error('Failed to calculate shipping rates');
    } finally {
      setIsCalculatingRates(false);
    }
  }, [watchedValues, userProfile]);

  // Form submission
  const onSubmit = async (data: CreateLabelForm) => {
    if (currentStep === 1) {
      // Move to packages step
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      // Move to service selection step and calculate rates
      setCurrentStep(3);
      // Calculate rates after moving to step 3
      setTimeout(() => calculateRates(), 100);
      return;
    }

    // Step 3: Create label
    try {
      // Get the shipping cost from the selected rate (round to 2 decimal places)
      const selectedRate = shippingRates[data.service_type];
      const shippingCost = selectedRate 
        ? parseFloat(((selectedRate.display_rate || selectedRate.total_charges)).toFixed(2))
        : null;
      
      const labelData = {
        to_name: data.to_name,
        to_company: data.to_company || '',
        to_address1: data.to_address1,
        to_address2: data.to_address2 || '',
        to_city: data.to_city,
        to_state: data.to_state,
        to_postal: data.to_postal,
        to_country: data.to_country,
        to_phone: data.to_phone,
        to_email: data.to_email || '',
        to_residential: data.to_residential || false,
        service_type: data.service_type,
        shipping_cost: shippingCost,
        reference_number: data.reference_number || '',
        reference_number_2: data.reference_number_2 || '',
        description: data.description || '',
        declared_value: data.declared_value ? parseFloat(data.declared_value.toString()) : null,
        packages: data.packages.map(pkg => ({
          weight: parseFloat(pkg.weight.toString()),
          weight_unit: pkg.weight_unit,
          length: parseFloat(pkg.length.toString()),
          width: parseFloat(pkg.width.toString()),
          height: parseFloat(pkg.height.toString()),
          dimension_unit: pkg.dimension_unit,
          description: pkg.description || '',
          quantity: parseInt(pkg.quantity.toString())
        }))
      };

      await createLabelMutation.mutateAsync(labelData);
    } catch (error) {
      console.error('Label creation error:', error);
    }
  };

  // Progress steps
  const steps = [
    { number: 1, title: 'Recipient Address', description: 'Enter recipient information' },
    { number: 2, title: 'Package Details', description: 'Enter package information' },
    { number: 3, title: 'Service & Create', description: 'Select service and create label' }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <ServiceStatusMonitor />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Create Shipping Label</h1>
        <p className="text-secondary-600">
          Generate UPS shipping labels quickly and easily
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors ${
                    currentStep >= step.number
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-white border-gray-300 text-gray-500'
                  }`}
                >
                  {currentStep > step.number ? (
                    <CheckCircle className="h-6 w-6" />
                  ) : (
                    <span className="text-lg font-semibold">{step.number}</span>
                    )}
                  </div>
                <div className="mt-2 text-center">
                  <div className="text-sm font-medium text-secondary-900">{step.title}</div>
                  <div className="text-xs text-secondary-600">{step.description}</div>
                </div>
              </div>
                  {index < steps.length - 1 && (
                <div
                  className={`h-1 w-full mx-4 transition-colors ${
                    currentStep > step.number ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                />
                  )}
                </div>
          ))}
          </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1: Recipient Address */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MapPin className="h-5 w-5" />
                <span>Recipient Address</span>
              </CardTitle>
              <CardDescription>
                Enter the recipient's information and address
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Recipient Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recipient Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                      Full Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                      {...register('to_name', validationRules.to_name)}
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                      Company Name (Optional)
                      </label>
                      <Input
                        {...register('to_company')}
                      placeholder="Company Inc."
                      />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      {...register('to_phone', { required: true })}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Email (Optional)
                    </label>
                    <Input
                      {...register('to_email')}
                      type="email"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                  </div>

              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
                <div className="grid grid-cols-1 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                      Address Line 1 <span className="text-red-500">*</span>
                      </label>
                      <Input
                      {...register('to_address1', validationRules.to_address1)}
                      placeholder="123 Main Street"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                      Address Line 2 (Optional)
                      </label>
                      <Input
                      {...register('to_address2')}
                      placeholder="Apt 4B, Suite 100, etc."
                      />
                    </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        City <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...register('to_city', validationRules.to_city)}
                        placeholder="New York"
                      />
                  </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        State <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...register('to_state', validationRules.to_state)}
                        placeholder="NY"
                        maxLength={2}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Postal Code <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...register('to_postal', validationRules.to_postal)}
                        placeholder="10001"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register('to_residential')}
                      className="h-4 w-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Residential Address
                    </label>
                  </div>

                  {isValidatingAddress && (
                    <div className="flex items-center space-x-2 text-blue-600">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Validating address...</span>
                    </div>
                  )}

                  {addressValidation && !isValidatingAddress && (
                    <div className={`p-4 rounded-md ${addressValidation.valid ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                      <div className="flex items-start space-x-3">
                        {addressValidation.valid ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className={`font-medium ${addressValidation.valid ? 'text-green-900' : 'text-yellow-900'}`}>
                            {addressValidation.message || (addressValidation.valid ? 'Address is valid' : 'Address may need correction')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-end pt-6 border-t">
                <Button
                  type="submit"
                  disabled={!isAddressValid}
                >
                  Next: Package Details
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Package Details */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Package className="h-5 w-5" />
                <span>Package Details</span>
              </CardTitle>
              <CardDescription>
                Enter package dimensions and weight
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Packages */}
              <div>
                  <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Package Information</h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                    onClick={() => append({
                      id: (fields.length + 1).toString(),
                      weight: 1,
                      weight_unit: 'lb',
                      length: 12,
                      width: 10,
                      height: 8,
                      dimension_unit: 'in',
                      description: '',
                      quantity: 1
                    })}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Package
                      </Button>
                  </div>

                <div className="space-y-6">
              {fields.map((field, index) => (
                    <div key={field.id} className="p-6 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                        <h4 className="text-md font-semibold text-gray-800">Package {index + 1}</h4>
                    {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                            onClick={() => remove(index)}
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove
                        </Button>
                    )}
                  </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                            Weight <span className="text-red-500">*</span>
                      </label>
                      <div className="flex space-x-2">
                        <Input
                          type="number"
                          step="0.1"
                              {...register(`packages.${index}.weight` as const, packageValidationRules.weight)}
                          placeholder="1.0"
                        />
                        <select
                              {...register(`packages.${index}.weight_unit` as const)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        >
                              <option value="lb">lb</option>
                          <option value="kg">kg</option>
                          <option value="oz">oz</option>
                          <option value="g">g</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                            Length <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                            step="0.1"
                            {...register(`packages.${index}.length` as const, packageValidationRules.length)}
                            placeholder="12.0"
                          />
                  </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                            Width <span className="text-red-500">*</span>
                      </label>
                        <Input
                          type="number"
                          step="0.1"
                            {...register(`packages.${index}.width` as const, packageValidationRules.width)}
                            placeholder="10.0"
                          />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                            Height <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        step="0.1"
                            {...register(`packages.${index}.height` as const, packageValidationRules.height)}
                            placeholder="8.0"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                            Dimension Unit
                          </label>
                          <select
                            {...register(`packages.${index}.dimension_unit` as const)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="in">Inches</option>
                            <option value="cm">Centimeters</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">
                            Quantity <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                            {...register(`packages.${index}.quantity` as const, packageValidationRules.quantity)}
                            placeholder="1"
                      />
                  </div>

                        <div className="space-y-2 md:col-span-2 lg:col-span-3">
                    <label className="text-sm font-medium text-gray-700">
                      Description (Optional)
                    </label>
                    <Input
                            {...register(`packages.${index}.description` as const)}
                            placeholder="Electronics, Clothing, etc."
                    />
                        </div>
                  </div>
                </div>
              ))}
                    </div>
              </div>
                  
              {/* Navigation */}
              <div className="flex justify-between pt-6 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setCurrentStep(1)}
                    >
                      Back: Address
                    </Button>
                    <Button
                  type="submit"
                  disabled={!validatePackages()}
                    >
                  Next: Service & Create
                    </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Service & Create */}
        {currentStep === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Truck className="h-5 w-5" />
                <span>Service & Create Label</span>
              </CardTitle>
              <CardDescription>
                Select shipping service and complete your label
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Service Selection */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Shipping Service</h3>
                
                {isCalculatingRates && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-3" />
                    <span className="text-gray-600">Loading available shipping methods...</span>
                  </div>
                )}

                {!isCalculatingRates && (
                  <div className="space-y-3">
                    {/* serviceOptions now only contains available services after rate calculation */}
                    {serviceOptions.map((service) => {
                        const rate = shippingRates[service.code];
                        const showRates = (userProfile as any)?.profile?.show_rates_to_user || userProfile?.show_rates_to_user;
                        const hasRate = showRates && rate;
                        
                        return (
                          <label
                            key={service.code}
                            className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              watchedValues.service_type === service.code
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <input
                                type="radio"
                                {...register('service_type', validationRules.service_type)}
                                value={service.code}
                                className="h-4 w-4 text-blue-500 border-gray-300 focus:ring-blue-500"
                              />
                              <div>
                                <div className="font-semibold text-gray-900">{service.name}</div>
                                <div className="text-sm text-gray-600">{service.description}</div>
                                {hasRate && rate.delivery_date && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    <Clock className="h-3 w-3 inline mr-1" />
                                    Est. Delivery: {rate.delivery_date}
                                  </div>
                                )}
                              </div>
                            </div>
                            {hasRate && (
                              <div className="text-right">
                                <div className="text-xl font-bold text-gray-900">
                                  ${rate.display_rate?.toFixed(2) || rate.total_charges.toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-500">{rate.currency || 'USD'}</div>
                              </div>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

              {/* Additional Details (Optional) */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Details (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Reference Number 1 (Optional)
                </label>
                <Input
                  {...register('reference_number')}
                  placeholder="REF123456"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Reference Number 2 (Optional)
                </label>
                <Input
                  {...register('reference_number_2')}
                  placeholder="CUST789012"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Description (Optional)
                </label>
                <Input
                  {...register('description')}
                  placeholder="Package description"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Declared Value (Optional)
                </label>
                <div className="flex space-x-2">
                  <span className="flex items-center px-3 py-2 bg-gray-100 border border-gray-300 rounded-l-md text-gray-700">
                    $
                  </span>
                  <Input
                    {...register('declared_value', {
                      min: { value: 0, message: 'Declared value must be non-negative' }
                    })}
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className="rounded-l-none"
                  />
                </div>
              </div>
                  </div>
              </div>
                
              {/* Navigation */}
              <div className="flex justify-between pt-6 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCurrentStep(2)}
                  >
                    Back: Packages
                  </Button>
                  <Button
                    type="submit"
                    loading={loadingStates.create_label?.isLoading}
                    disabled={!isFormValidWithPackages || createLabelMutation.isPending}
                    className="flex items-center space-x-2"
                  >
                    <span>Create Label</span>
                    <Truck className="h-4 w-4" />
                  </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </form>
    </div>
  );
};

export default CreateLabelOptimized;
